import React, { createContext, useState, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0d6efd',
      light: '#e7f0ff', // A lighter shade for backgrounds
      dark: '#0056b3',  // A darker shade for hover/active states
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#fd7e14', // eYantra's accent orange
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8f9fa', // Light grey background for the page
      paper: '#ffffff',   // White for cards and surfaces
    },
    text: {
      primary: '#212529', // Dark text for readability
      secondary: '#6c757d', // Grey for secondary text
    },
    divider: 'rgba(0, 0, 0, 0.12)',
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 700 },
    h2: { fontSize: '2rem', fontWeight: 700, color: '#212529' },
    h3: { fontSize: '1.75rem', fontWeight: 600, color: '#007bff' },
    h4: { fontSize: '1.5rem', fontWeight: 600, color: '#212529' },
    h5: { fontSize: '1.25rem', fontWeight: 600, color: '#212529' },
    h6: { fontSize: '1rem', fontWeight: 600, color: '#212529' },
    metricLabel: { // Custom Variant
      fontSize: '0.9em',
      color: '#555555',
      textTransform: 'capitalize',
      fontWeight: 400,
    },
    enquiryValue: { // Custom Variant
       fontSize: '1.1rem',
       fontWeight: 700,
       color: '#0056b3',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundImage: 'none',
          boxShadow: '0px 4px 12px rgba(0,0,0,0.05)',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
        },
        containedSecondary: {
            color: '#ffffff',
        }
      },
    },
    MuiDrawer: {
        styleOverrides: {
            paper: {
                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                backgroundColor: '#ffffff',
            }
        }
    },
    MuiListItemButton: {
        styleOverrides: {
            root: {
                borderRadius: 8,
                '&.Mui-selected': {
                    backgroundColor: '#e7f0ff',
                    '&:hover': {
                        backgroundColor: '#d0e0ff',
                    }
                }
            }
        }
    }
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#7A77FF', // Subtle purple/blue
    },
    secondary: {
      main: '#fd7e14', // eYantra's accent orange for specific buttons
    },
    background: {
      default: '#1E1E2D', // Main dark background
      paper: '#27293D',   // Card and surface background
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#A9A9B3',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 700 },
    h2: { fontSize: '2rem', fontWeight: 700 },
    h3: { fontSize: '1.75rem', fontWeight: 600 },
    h4: { fontSize: '1.5rem', fontWeight: 600 },
    h5: { fontSize: '1.25rem', fontWeight: 600 },
    h6: { fontSize: '1rem', fontWeight: 600 },
    metricLabel: { // Custom Variant
      fontSize: '0.9em',
      color: '#A9A9B3', // Adjusted for dark theme
      textTransform: 'capitalize',
      fontWeight: 400,
    },
    enquiryValue: { // Custom Variant
       fontSize: '1.1rem',
       fontWeight: 700,
       color: '#7A77FF', // Adjusted for dark theme
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
        defaultProps: {
            variant: 'outlined',
        }
    }
  },
});

export const ThemeContext = createContext({
  toggleTheme: () => {},
});

export const AppThemeProvider = ({ children }) => {
  const [mode, setMode] = useState('light');

  const theme = useMemo(() => (mode === 'light' ? lightTheme : darkTheme), [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ toggleTheme, mode }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};